import { m } from 'framer-motion';
import { useMemo, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import { Avatar } from '@mui/material';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useBoolean } from 'src/hooks/use-boolean';

import { useNotificationOperations } from 'src/api/services/system_definition/systemService';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { varHover } from 'src/components/animate';

import { NotificationTargetSelector } from './notification-user-selector';
import {
  useGetUserNotificationReceipents,
  useGetParticipantNotificationReceipents,
} from '../../../../modules/admin/services/adminService';

// ----------------------------------------------------------------------

// Get unique roles from users
const getRoles = (users) => {
  if (!users || users.length === 0) return [];
  const roles = new Set();
  users.forEach((user) => {
    const roleName = user.Role?.Name;
    if (roleName) {
      roles.add(roleName);
    }
  });
  return Array.from(roles).sort();
};

// Filter users based on role and search query
const getFilteredUsers = (users, roleFilter, searchQuery) => {
  if (!users || users.length === 0) return [];

  let filtered = users;

  // Apply role filter
  if (roleFilter !== 'all') {
    filtered = filtered.filter((user) => {
      const roleName = user.Role?.Name;
      return roleName === roleFilter;
    });
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const searchLower = searchQuery.toLowerCase().trim();
    filtered = filtered.filter((user) => {
      const firstName = (user.FirstName || '').toLowerCase();
      const lastName = (user.LastName || '').toLowerCase();
      const email = (user.Email || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`.toLowerCase();

      return (
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        email.includes(searchLower) ||
        fullName.includes(searchLower)
      );
    });
  }

  return filtered;
};

// Get role icon based on role name
const getRoleIcon = (roleName) => {
  switch (roleName) {
    case 'Admin':
      return 'mdi:shield-crown';
    case 'Student':
      return 'mdi:school';
    case 'Teacher':
    case 'Supervisor':
      return 'mdi:account-tie';
    case 'Judge':
      return 'mdi:gavel';
    default:
      return 'mdi:account';
  }
};

export function NotificationButton({ ...other }) {
  const dialog = useBoolean();
  const { users } = useGetUserNotificationReceipents();
  const { participants } = useGetParticipantNotificationReceipents();
  console.log('asdasd', participants);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [notificationType, setNotificationType] = useState('');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);

  // Filter states
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const notificationTypes = [
    {
      Id: 'Success',
      Name: 'Success',
      Icon: 'solar:check-circle-bold',
      Color: 'success.main',
      Bgcolor: 'success.lighter',
    },
    {
      Id: 'Info',
      Name: 'Info',
      Icon: 'solar:info-circle-bold',
      Color: 'info.main',
      Bgcolor: 'info.lighter',
    },
    {
      Id: 'Warning',
      Name: 'Warning',
      Icon: 'solar:danger-triangle-bold',
      Color: 'warning.main',
      Bgcolor: 'warning.lighter',
    },
    {
      Id: 'Error',
      Name: 'Error',
      Icon: 'solar:close-circle-bold',
      Color: 'error.main',
      Bgcolor: 'error.lighter',
    },
  ];

  // Extract unique roles from users
  const availableRoles = useMemo(() => getRoles(users || []), [users]);

  // Filter users based on role and search query
  const filteredUsers = useMemo(
    () => getFilteredUsers(users || [], roleFilter, searchQuery),
    [users, roleFilter, searchQuery]
  );

  // Check if all filtered users are selected
  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedUsers.includes(u.Id));

  // Check if any filter is active
  const hasActiveFilter = roleFilter !== 'all' || searchQuery.trim();

  const { sendNotification } = useNotificationOperations();

  // Handle user selection toggle
  const handleUserToggle = useCallback((userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      return [...prev, userId];
    });
  }, []);

  // Handle select all toggle
  const handleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      // Deselect all filtered users
      const filteredUserIds = filteredUsers.map((u) => u.Id);
      setSelectedUsers((prev) => prev.filter((id) => !filteredUserIds.includes(id)));
    } else {
      // Select all filtered users
      const filteredUserIds = filteredUsers.map((u) => u.Id);
      setSelectedUsers((prev) => {
        const newSelected = [...prev];
        filteredUserIds.forEach((id) => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  }, [filteredUsers, allFilteredSelected]);

  const handleClose = useCallback(() => {
    dialog.onFalse();
    setSelectedUsers([]);
    setNotificationType('');
    setTitle('');
    setMessage('');
    setRoleFilter('all');
    setSearchQuery('');
  }, [dialog]);

  const handleSubmit = useCallback(async () => {
    if (!notificationType.trim() || !title.trim() || !message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setLoading(true);
    try {
      await sendNotification({
        Type: notificationType,
        Title: title.trim(),
        Message: message.trim(),
        UserIds: selectedUsers,
      });

      toast.success('Notification sent!');
      handleClose();
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error?.ExceptionMessage || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  }, [notificationType, message, title, selectedUsers, sendNotification, handleClose]);

  const isValid =
    notificationType.trim().length > 0 &&
    title.trim().length > 0 &&
    message.trim().length > 0 &&
    selectedUsers.length > 0;

  return (
    <>
      <Tooltip title="Send Notification" arrow>
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={dialog.onTrue}
          sx={{ width: 48, height: 48 }}
          {...other}
        >
          <Iconify icon="solar:add-circle-bold-duotone" width={40} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={dialog.value}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.lighter',
                color: 'primary.main',
              }}
            >
              <Iconify icon="solar:bell-bold-duotone" width={28} />
            </Box>
            <Box>
              <Box sx={{ typography: 'h6' }}>Send Notification</Box>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            pt: 3,
            pb: 3,
          }}
        >
          <Box
            rowGap={3}
            columnGap={2}
            display="grid"
            sx={{ alignItems: 'flex-start' }}
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
          >
            <Stack spacing={3}>
              {/* Notification Type Selection */}
              <TextField
                select
                fullWidth
                label="Notification Type"
                value={notificationType}
                onChange={(e) => setNotificationType(e.target.value)}
                helperText="Select a category for your notification"
              >
                {notificationTypes?.map((type) => (
                  <MenuItem key={type.Id} value={type.Id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          color: type.Color,
                          bgcolor: type.Bgcolor,
                        }}
                      >
                        <Iconify icon={type.Icon} width={24} />
                      </Avatar>
                      <Box>{type.Name}</Box>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>

              {/* Notification Title & Message */}
              <TextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
                placeholder="Title of the notification..."
                inputProps={{
                  maxLength: 50,
                }}
              />
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={8}
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={loading}
                placeholder="Write your notification message..."
                helperText={`${message.length} / 2000 characters`}
                inputProps={{
                  maxLength: 2000,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    alignItems: 'flex-start',
                  },
                }}
              />
            </Stack>

            <NotificationTargetSelector
              users={users}
              participants={participants}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!isValid || loading}
            startIcon={
              loading ? (
                <Iconify icon="svg-spinners:180-ring-with-bg" width={20} />
              ) : (
                <Iconify icon="mingcute:send-fill" width={20} />
              )
            }
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Sending...' : 'Send Notification'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
